/**
 * 心理スコアリングルール (Issue #14)。
 *
 * 便器割当時にルールを判定し、スコア加算と AppliedRule 記録を返す。
 */
import type { AppliedRule, Char, RuleId, Urinal } from './types'

/** ルール定義テーブル。 */
const RULES: Record<
  RuleId,
  { score: number; description: string; shortLabel: string }
> = {
  END_URINAL: {
    score: 10,
    description: '端の便器を選んだ: +10',
    shortLabel: '端の便器',
  },
  /**
   * @future 便器5台以上構成（Issue #32）で有効化予定。
   * 現在の4台構成では到達不能（SAME_COLUMN_TABOO が先に評価されるため）。
   */
  NEIGHBOR_EMPTY: {
    score: 5,
    description: '隣を1つ空けて使用: +5',
    shortLabel: '隣を1つ空け',
  },
  NO_NEIGHBOR: {
    score: 20,
    description: '完全に隣がいない状態: +20',
    shortLabel: '完全に隣なし',
  },
  SAME_COLUMN_TABOO: {
    score: -15,
    description: '隣に人がいる状態に誘導: -15',
    shortLabel: '直接隣接',
  },
  FORCED_ADJACENT: {
    score: 0,
    description: '選択肢なし (隣接は仕方ない): ±0',
    shortLabel: '選択肢なし',
  },
}

/**
 * 便器割当時にルールを判定し、スコア差分と適用ルールリストを返す。
 *
 * @param urinalId  割り当てる便器 ID
 * @param urinals   全便器リスト
 * @param char      割り当てるキャラクター（省略可）。GROUP タイプ時は隣接ペナルティを免除。
 * @returns { scoreDelta, rules }
 */
export function evaluateAssignment(
  urinalId: number,
  urinals: Urinal[],
  char?: Char
): { scoreDelta: number; rules: AppliedRule[] } {
  const applied: AppliedRule[] = []
  let total = 0

  const target = urinals.find(u => u.id === urinalId)
  if (!target) return { scoreDelta: 0, rules: [] }

  // 隣接便器 (左右1つ) を取得。
  const left = urinals.find(u => u.id === urinalId - 1)
  const right = urinals.find(u => u.id === urinalId + 1)
  const leftOccupied = left?.state === 'OCCUPIED'
  const rightOccupied = right?.state === 'OCCUPIED'
  const hasLeft = left !== undefined
  const hasRight = right !== undefined

  // 端の便器判定 (左端 or 右端)。
  const isEnd = !hasLeft || !hasRight
  if (isEnd) {
    const r = applyRule('END_URINAL', urinalId)
    applied.push(r)
    total += r.scoreDelta
  }

  // 強制隣接判定: 両隣が全部埋まっている場合はペナルティなし。
  const forcedAdjacent = hasLeft && leftOccupied && hasRight && rightOccupied
  if (forcedAdjacent) {
    const r = applyRule('FORCED_ADJACENT', urinalId)
    applied.push(r)
    total += r.scoreDelta
    return { scoreDelta: total, rules: applied }
  }

  // 隣が誰もいない状態。
  const noNeighbor = !leftOccupied && !rightOccupied
  if (noNeighbor) {
    const r = applyRule('NO_NEIGHBOR', urinalId)
    applied.push(r)
    total += r.scoreDelta
  } else {
    // 直接隣（distance=1）に人がいる = タブー。
    // NEIGHBOR_EMPTY は「1つ空けた (distance ≥ 2)」が理想だが
    // 4台構成では両隣が必ず distance=1 のため適用対象なし。
    // 直接隣接ペナルティとして SAME_COLUMN_TABOO を適用する。
    // ただし GROUP タイプは団体客なので隣接ペナルティを免除する。
    if (char?.type !== 'GROUP') {
      const r = applyRule('SAME_COLUMN_TABOO', urinalId)
      applied.push(r)
      total += r.scoreDelta
    }
  }

  return { scoreDelta: total, rules: applied }
}

function applyRule(ruleId: RuleId, urinalId: number): AppliedRule {
  const def = RULES[ruleId]
  return {
    ruleId,
    urinalId,
    scoreDelta: def.score,
    shortLabel: def.shortLabel,
    description: def.description,
  }
}
